import { useToast } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { nameIn } from './categories.ts';
import type { Product, ProductStatus } from './product.ts';
import { queryKeys, usePageContext } from '../usePageContext.ts';

export type StatusFailure = { readonly product: Product; readonly error: unknown };

export type StatusOutcome = {
  readonly changed: readonly Product[];
  readonly failed: readonly StatusFailure[];
};

type Previous = { readonly product: Product; readonly status: ProductStatus };

const useProductStatus = (api: TypedClient) => {
  const { tenantId, queryClient, invalidate } = usePageContext();

  const change = async (product: Product, status: ProductStatus): Promise<Product> => {
    const saved = await api.post('/admin/products/{id}/status', {
      path: { id: product.id },
      body: { version: product.version, status },
    });
    queryClient.setQueryData(queryKeys.product(tenantId, saved.id), saved);
    return saved;
  };

  const revert = async ({ product, status }: Previous): Promise<Product> => {
    const reopened = product.status === 'archived' ? await change(product, 'draft') : product;
    return reopened.status === status ? reopened : change(reopened, status);
  };

  const settle = async (
    products: readonly Product[],
    step: (product: Product) => Promise<Product>,
  ): Promise<StatusOutcome> => {
    const results = await Promise.allSettled(products.map(step));
    void invalidate('products', 'dashboard');
    return {
      changed: results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : [])),
      failed: results.flatMap((result, position) => {
        const product = products[position];
        return result.status === 'rejected' && product !== undefined
          ? [{ product, error: result.reason as unknown }]
          : [];
      }),
    };
  };

  return {
    changeAll: (products: readonly Product[], status: ProductStatus) =>
      settle(products, (product) => change(product, status)),
    revertAll: (changed: readonly Product[], before: readonly Product[]) => {
      const statusBefore = new Map(before.map((product) => [product.id, product.status]));
      return settle(changed, (product) =>
        revert({ product, status: statusBefore.get(product.id) ?? product.status }),
      );
    },
  };
};

export const useProductStatusActions = (api: TypedClient) => {
  const { t, locale, notify, describeError } = usePageContext();
  const toast = useToast();
  const status = useProductStatus(api);

  const reportFailures = (failed: readonly StatusFailure[]) => {
    for (const { product, error } of failed)
      toast.notify({
        tone: 'danger',
        message: t('productStatus.failed', {
          name: nameIn(product, locale, product.reference ?? product.id),
          reason: describeError(error) ?? t('errors.UNKNOWN'),
        }),
      });
  };

  const undo = async (changed: readonly Product[], before: readonly Product[]) => {
    const outcome = await status.revertAll(changed, before);
    reportFailures(outcome.failed);
    if (outcome.changed.length > 0) notify(t('productStatus.restored', { count: outcome.changed.length }));
  };

  return {
    publish: async (products: readonly Product[]): Promise<StatusOutcome> => {
      const outcome = await status.changeAll(products, 'active');
      reportFailures(outcome.failed);
      if (outcome.changed.length > 0) notify(t('productStatus.published', { count: outcome.changed.length }));
      return outcome;
    },
    deactivate: async (products: readonly Product[]): Promise<StatusOutcome> => {
      const outcome = await status.changeAll(products, 'draft');
      reportFailures(outcome.failed);
      if (outcome.changed.length > 0)
        notify(t('productStatus.deactivated', { count: outcome.changed.length }));
      return outcome;
    },
    archive: async (products: readonly Product[]): Promise<StatusOutcome> => {
      const outcome = await status.changeAll(products, 'archived');
      reportFailures(outcome.failed);
      if (outcome.changed.length > 0)
        toast.notify({
          tone: 'success',
          message: t('productStatus.archived', { count: outcome.changed.length }),
          actionLabel: t('productEditor.undo'),
          onAction: () => {
            void undo(outcome.changed, products);
          },
        });
      return outcome;
    },
  };
};
